//
// # GenAlpha Schema
// ## Core/ Reused Types
//

// NPM Imports
import typia from "typia";

// Local Imports
import { SchemaVersion } from "./version";

// # Transform
// Combination of a location and orientation
export type Xform = Point & Orientation;

// # Point
// Two-dimensional point in schematic-space.
export interface Point {
  x: number;
  y: number;
}

// # Orientation
// including reflection & 90 degree increment rotation
export interface Orientation {
  reflected: boolean; // Vertical reflection, applied before rotation. Defaults to false.
  rotation: Rotation; // Rotation in increments of 90 degrees. Defaults to 0.
}

// Enumerated Rotations
// in increments of 90 degrees
export type Rotation = 0 | 90 | 180 | 270;

export interface Bbox {
  min: Point;
  max: Point;
}

// # Selection Bounding Box
// Extends Bbox with an optional ID for change-pipeline tracking.
// Used by symbol selection boxes, which need IDs for add/remove/update changes.
export interface SelectionBbox extends Bbox {
  id?: Id;
}

// # Graphics Element Union
export type Element = Shape | Text;

// # Shape Elements
export type Shape = Rectangle | Polygon | Arc | Ellipse | Path;
export type ShapeKind = Shape["kind"];

// # Enumerated Graphics Element Kinds & Tags
export type ElementKind = Element["kind"];

export const ElementKind = {
  Rectangle: "rectangle",
  Polygon: "polygon",
  Path: "path",
  Ellipse: "ellipse",
  Arc: "arc",
  Text: "text",
} as const;

export interface Rectangle {
  kind: typeof ElementKind.Rectangle;
  bbox: Bbox;
  filled: boolean;
  id?: Id;
  groupId?: Id;
}

export interface Ellipse {
  kind: typeof ElementKind.Ellipse;
  bbox: Bbox;
  filled: boolean;
  id?: Id;
  groupId?: Id;
}

export interface Arc {
  kind: typeof ElementKind.Arc;
  ellipseBbox: Bbox;
  arcBbox: Bbox;
  startAngle: number;
  stopAngle: number;
  id?: Id;
  groupId?: Id;
}

// # Path
//
// An unclosed, parametric-width array of points, plus end-styling data.
// Not the most generic `path` as implemented by SVG, canvas, etc.
// Lines between each point are straight.
//
export interface Path {
  kind: typeof ElementKind.Path;
  points: Array<Point>;
  style: PathStyle;
  id?: Id;
  groupId?: Id;
}

export interface PathStyle {
  width: number;
  beginExt: number;
  endExt: number;
}
export interface Polygon {
  kind: typeof ElementKind.Polygon;
  points: Array<Point>;
  filled: boolean;
  id?: Id;
  groupId?: Id;
}

// # Text Element
export type Text = TextFormat & {
  text: string;
  kind: typeof ElementKind.Text;
  id?: Id;
  groupId?: Id;
};

// # Text Format
// Everything about a text label, except for the text-string itself.
export type TextFormat = TextOrientation & {
  loc: Point;
  height: number;
  hidden: boolean; // Visibility. Defaults to visible/ hidden = false.
};

// # Text Orientation
//
// Specifies vertical and horizontal alignment, and rotation.
// All text reads one of two ways: left to right, or bottom to top.
// Text can therefore either be rotated either 0 or 90 degrees from its parent element.
//
export interface TextOrientation {
  alignment: Alignment; // Horizontal Alignment: left, center, or right
  baseline: Baseline; // Vertical Baseline: top, middle, or bottom
  rotation: TextRotation; // Rotation: 0 or 90 degrees
}

export type Alignment = "left" | "center" | "right";
export type Baseline = "top" | "middle" | "bottom";
export type TextRotation = 0 | 90;

export type Direction = "horiz" | "vert";

export type Layer = number;
export type Layered = { layer: Layer };
export type LayeredRectangle = Layered & Rectangle;
export type LayeredPath = Layered & Path;
export type LayeredPolygon = Layered & Polygon;
type LayeredShape = Layered & Shape;
type LayeredText = Layered & Text;
export type LayeredElement = LayeredShape | LayeredText;
// export type LayeredEllipse = Layered & Ellipse;
// export type LayeredArc = Layered & Arc;

export type Id = number; // IDs

export interface GraphicsContent {
  schemaVersion: SchemaVersion; // Required for all content
  contentKind: "graphics";
  ident: Ident; // Identifier
  elements: Array<Element>;
  properties?: Array<Property>; // Properties
}

// # Enumerated Port Kinds/ Directions
export type PortDir = "input" | "output" | "inout";
export const PortDir = {
  Input: "input",
  Output: "output",
  Inout: "inout",
} as const;

// # Pin Type
// Semantic classification of pins for power routing and analysis.
// If not specified, defaults to "signal" at the consumer level.
export type PinType = "signal" | "power" | "ground" | "clock";
export const PinType = {
  Signal: "signal",
  Power: "power",
  Ground: "ground",
  Clock: "clock",
} as const;

// # Identifier
export type Ident = BuiltinIdent | LibCellViewIdent | UuidIdent | HierPathIdent;

export type BuiltinId =
  | "res"
  | "res3"
  | "nmos"
  | "pmos"
  | "npn"
  | "pnp"
  | "cap"
  | "cap3"
  | "ind"
  | "vdd"
  | "gnd"
  | "ccs"
  | "vdc"
  | "no_connect";
export type BuiltinIdent = { kind: "builtin"; id: BuiltinId };

// # Unique ID Identifiers
export type UuidIdent = {
  kind: "uuid";
  id: Uuid; // Unique ID
  name: string; // Display Name
};

// (Lib, Cell, View) Triplet
export type LibCellViewIdent = { kind: "libcellview" } & LibCellView;

// # Hierarchical Path Identifiers
// Arbitrary nesting of content using string path segments
export type HierPathIdent = {
  kind: "hierpath";
  path: string[]; // e.g. ["package2", "subpackage1", "module5", "layout3"]
};

// # Library, Cell, and View Triplet Identifiers
export interface LibCellView {
  lib: string;
  cell: string;
  view: string;
}

// UUID-format string
export type Uuid = string & typia.tags.Format<"uuid">;

//export type StackId = string & { readonly __brand: "StackId" };
export type StackId = string;

// # Property
// The schema extension mechanism.
// Key-value pairs where the key is a string, and the value is any JSON-serializable value.
export interface Property {
  name: string; // Property Name
  value: unknown; // Value. Anything serializable to JSON.
}

export const SignalType = PinType;
export type SignalType = PinType;

// # OA Term Residue
//
// OA term types and signal types a symbol or schematic carried that GenAlpha
// has no value for. The import takes `inout`, or `signal`, and keeps the
// original here; the export writes it back while the GenAlpha value is still
// the one the import took. A symbol has no signal types, so a symbol import
// keeps every net's OA signal type other than `signal`.
export interface OaTermResidue {
  termTypes?: Array<OaRawTermType>; // `noa.Term.Type` values by term name
  signalTypes?: Array<OaRawSignalType>; // `noa.SignalType` values by net name
}

export interface OaRawTermType {
  term: string;
  termType: number;
}

export interface OaRawSignalType {
  net: string;
  signalType: number;
}
